import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { todoApi } from '../api/todoApi';
import { PublicTodoList as PublicTodoListType, PublicTodoItem } from '../types/todo';
import { getListColorByValue } from '../utils/colorUtils';
import { formatDateForDisplay, groupTodosByDueDate } from '../utils/dateUtils';
import { parseTags } from '../utils/tagUtils';
import { ChevronDown, ChevronRight, Check, Lock, ExternalLink } from 'lucide-react';
import { StatsChart } from './StatsChart';
import { Loader } from '@mantine/core';
import { ClickableText } from '../utils/linkUtils';
import './TodoList.css';
import './PublicTodoList.css';

interface PublicTodoListProps {
  token: string;
}

interface PublicSectionProps {
  title: string;
  todos: PublicTodoItem[];
  defaultCollapsed: boolean;
  onShowComments: (todo: PublicTodoItem) => void;
}

const PublicSection: React.FC<PublicSectionProps> = ({ title, todos, defaultCollapsed, onShowComments }) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <div className="public-todo-section">
      <div 
        className="public-todo-section-header"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="public-todo-section-title">
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          <span>{title}</span>
          <span className="public-todo-count">({todos.length})</span>
        </div>
      </div>
      
      {!isCollapsed && (
        <div className="public-todo-section-items">
          {todos.map((todo, index) => (
            <div key={index} className="public-todo-item-wrapper">
              <PublicTodoItemComponent todo={todo} onShowComments={() => onShowComments(todo)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface PublicTodoItemComponentProps {
  todo: PublicTodoItem;
  onShowComments: () => void;
}

const PublicTodoItemComponent: React.FC<PublicTodoItemComponentProps> = ({ todo, onShowComments }) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return '#ff4757';
      case 'Medium': return '#ffa502';
      case 'Low': return '#26de81';
      default: return '#747d8c';
    }
  };

  const getDisplayName = (emailOrName: string) => {
    // If it contains @, extract the part before @, otherwise return as is
    if (emailOrName.includes('@')) {
      return emailOrName.split('@')[0];
    }
    return emailOrName;
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && !todo.isCompleted;
  };

  const isDueSoon = (dueDate?: string) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const now = new Date();
    const diffHours = (due.getTime() - now.getTime()) / (1000 * 60 * 60);
    return diffHours > 0 && diffHours <= 24 && !todo.isCompleted;
  };

  const formatDate = (dateString: string) => {
    return formatDateForDisplay(dateString);
  };

  return (
    <div className={`public-todo-item ${todo.isCompleted ? 'public-todo-completed' : ''}`}>
      <div className="public-todo-checkbox">
        {todo.isCompleted && <Check size={18} />}
      </div>

      <div className="public-todo-main-content">
        <div className="public-todo-header-row">
          <div className="public-todo-title-container">
            <h3 className="public-todo-title">{todo.title}</h3>
            {todo.tags && (
              <div className="public-todo-tags">
                {parseTags(todo.tags).map((tag: string, index: number) => {
                  // Generate a consistent color based on tag text
                  const getTagHue = (tagText: string) => {
                    let hash = 0;
                    for (let i = 0; i < tagText.length; i++) {
                      hash = tagText.charCodeAt(i) + ((hash << 5) - hash);
                    }
                    return Math.abs(hash) % 360;
                  };
                  
                  return (
                    <span 
                      key={index} 
                      className="public-todo-tag"
                      style={{ '--tag-hue': getTagHue(tag.toLowerCase()) } as React.CSSProperties}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {todo.description && todo.description.trim() && (
          <p className="public-todo-description">
            <ClickableText>{todo.description}</ClickableText>
          </p>
        )}

        <div className="public-todo-meta">
          {(todo.priority === 'Low' || todo.priority === 'High') && (
            <span 
              className="public-todo-priority"
              style={{ backgroundColor: getPriorityColor(todo.priority) }}
            >
              {todo.priority}
            </span>
          )}

          {todo.assignedTo && (
            <span className="public-todo-assignee">
              👤 {getDisplayName(todo.assignedTo)}
            </span>
          )}

          {todo.dueDate && (
            <span 
              className={`public-todo-due-date ${isOverdue(todo.dueDate) ? 'public-todo-overdue' : isDueSoon(todo.dueDate) ? 'public-todo-due-soon' : ''}`}
            >
              📅 {formatDate(todo.dueDate)}
            </span>
          )}

          {todo.comments.length > 0 && (
            <span 
              className="public-todo-comments"
              onClick={onShowComments}
              style={{ cursor: 'pointer' }}
              title={`Click to view ${todo.comments.length} comment${todo.comments.length !== 1 ? 's' : ''}`}
            >
              💬 {todo.comments.length} comment{todo.comments.length !== 1 ? 's' : ''}
            </span>
          )}

          {todo.isCompleted && todo.completedAt && (
            <span className="public-todo-completion">
              ✓ {formatDate(todo.completedAt)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const PublicTodoList: React.FC<PublicTodoListProps> = ({ token }) => {
  const [selectedTodoComments, setSelectedTodoComments] = useState<PublicTodoItem | null>(null);

  const getDisplayName = (emailOrName: string) => {
    // If it contains @, extract the part before @, otherwise return as is
    if (emailOrName.includes('@')) {
      return emailOrName.split('@')[0];
    }
    return emailOrName;
  };

  const handleShowComments = (todo: PublicTodoItem) => {
    setSelectedTodoComments(todo);
  };

  const handleCloseComments = () => {
    setSelectedTodoComments(null);
  };

  const formatDate = (dateString: string) => {
    return formatDateForDisplay(dateString);
  };

  const { data: list, isLoading, error } = useQuery<PublicTodoListType>({
    queryKey: ['publicList', token],
    queryFn: () => todoApi.getPublicList(token),
    staleTime: 30000, // 30 seconds
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw'
      }}>
        <Loader type="bars" size="lg" />
      </div>
    );
  }

  if (error || !list) {
    return (
      <div className="public-todo-error-container">
        <div className="public-todo-error-message">
          <Lock size={48} />
          <h2>List Not Found</h2>
          <p>This public list could not be found or is no longer available.</p>
          <p className="public-todo-error-cta">
            Grab a fresh one at{' '}
            <a
              href="https://symphonytodo.com"
              className="public-todo-error-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              symphonytodo.com
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  const listColor = getListColorByValue(list.color || '#3B82F6');
  const todos = list.items || [];

  // Group todos using the same logic as the main todo list
  const incompleteTodos = todos.filter(todo => !todo.isCompleted);
  const completedTodos = todos.filter(todo => todo.isCompleted);
  const groupedTodos = groupTodosByDueDate(incompleteTodos, 'rank', completedTodos);

  // Generate dynamic meta data for social media sharing
  const totalTasks = todos.length;
  const completedTasks = completedTodos.length;
  const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const creatorName = getDisplayName(list.createdBy);
  
  const pageTitle = `${list.name} - Shared Todo List`;
  const pageDescription = list.description 
    ? `${list.description} | ${progressPercentage}% complete (${completedTasks}/${totalTasks} tasks) | Shared by ${creatorName}`
    : `${progressPercentage}% complete (${completedTasks}/${totalTasks} tasks) | Shared by ${creatorName} on SymphonyTodo`;
    
  const currentUrl = window.location.href;

  // Create developer-focused section names that showcase the work pipeline
  const funSections = [
    {
      title: '🚨 Overdue',
      todos: groupedTodos.overdue,
      defaultCollapsed: false,
      key: 'overdue'
    },
    {
      title: '🎯 Today',
      todos: groupedTodos.today,
      defaultCollapsed: false,
      key: 'today'
    },
    {
      title: '📅 Tomorrow\'s Plans',
      todos: groupedTodos.tomorrow,
      defaultCollapsed: false,
      key: 'tomorrow'
    },
    {
      title: '🏖️ Weekend Chillout Projects - Side Quests',
      todos: groupedTodos.thisWeekend,
      defaultCollapsed: false,
      key: 'thisWeekend'
    },
    {
      title: '🗓️ Scheduled for Later...',
      todos: groupedTodos.later,
      defaultCollapsed: false,
      key: 'later'
    },
    {
      title: '💡 Items without a date',
      todos: groupedTodos.noDueDate,
      defaultCollapsed: false,
      key: 'noDueDate'
    },
    {
      title: '✅ Completed!!',
      todos: groupedTodos.completed,
      defaultCollapsed: true,
      key: 'completed'
    }
  ].filter(section => section.todos.length > 0); // Only show sections with todos

  return (
    <div 
      className="public-todo-list-wrapper"
      style={{ '--public-list-color': listColor.value } as React.CSSProperties}
    >
      {/* Dynamic meta tags for social media sharing */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph meta tags for Facebook, LinkedIn, etc. */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:site_name" content="SymphonyTodo" />
        <meta property="og:image" content="https://symphonytodo.com/icons/icon-512x512.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content={`${list.name} - Todo List Progress`} />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://symphonytodo.com/icons/icon-512x512.png" />
        <meta name="twitter:image:alt" content={`${list.name} - Todo List Progress`} />
        
        {/* Additional meta tags */}
        <meta name="author" content={creatorName} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" content={currentUrl} />
      </Helmet>
      <div className="public-todo-scrollable-container">
        {/* Header Banner */}
        <div className="public-todo-header">
          <div className="public-todo-header-content">
            <div className="public-todo-list-info">
              <div 
                className="public-todo-color-indicator" 
                style={{ backgroundColor: listColor.value }}
              />
              <div className="public-todo-list-details">
                <h1 className="public-todo-list-title">{list.name}</h1>
                {list.description && (
                  <p className="public-todo-list-description">{list.description}</p>
                )}
                <div className="public-todo-list-meta">
                  <span>Created by {getDisplayName(list.createdBy)}</span>
                  <span>•</span>
                  <span>{formatDateForDisplay(list.createdAt)}</span>
                  <span>•</span>
                  <span className="public-todo-readonly-badge">
                    <Lock size={12} />
                    Read-only
                  </span>
                  <span>•</span>
                  <StatsChart todos={todos.map((todo, index) => ({
                    ...todo,
                    id: index,
                    listId: 0,
                    listName: list.name,
                    rank: index,
                    comments: todo.comments.map((comment, commentIndex) => ({
                      ...comment,
                      id: commentIndex,
                      todoItemId: index
                    })),
                    subTasks: [],
                    completedSubTasksCount: 0,
                    totalSubTasksCount: 0,
                  }))}>
                    <div className="context-stats">
                      <div className="stats-mini-chart">
                        {/* Simple 7-day completion chart */}
                        {Array.from({ length: 7 }, (_, i) => {
                          const date = new Date();
                          date.setDate(date.getDate() - (6 - i));
                          const dayTodos = todos.filter(todo => {
                            if (!todo.completedAt) return false;
                            const completedDate = new Date(todo.completedAt);
                            const completedDay = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate());
                            const currentDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                            return completedDay.getTime() === currentDay.getTime();
                          });
                          const height = dayTodos.length === 0 ? 2 : Math.max(2, Math.min(12, dayTodos.length * 3));
                          return (
                            <div
                              key={i}
                              className="mini-bar"
                              style={{
                                height: `${height}px`,
                                backgroundColor: dayTodos.length === 0 ? '#e2e8f0' : '#3b82f6'
                              }}
                            />
                          );
                        })}
                      </div>
                      <span className="stats-text">
                        {todos.filter(t => t.isCompleted).length}/{todos.length} completed
                      </span>
                    </div>
                  </StatsChart>
                </div>
              </div>
            </div>
            <div className="public-todo-branding">
              <a 
                href="https://symphonytodo.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="public-todo-brand-link"
              >
                <ExternalLink size={16} />
                SymphonyTodo
              </a>
            </div>
          </div>
        </div>

        {/* Todo List Content */}
        <div className="public-todo-content">
          {funSections.length === 0 ? (
            <div className="public-todo-empty-state">
              <p>This list is empty.</p>
            </div>
          ) : (
            <>
              {funSections.map((section) => (
                <PublicSection
                  key={section.key}
                  title={section.title}
                  todos={section.todos}
                  defaultCollapsed={section.defaultCollapsed}
                  onShowComments={handleShowComments}
                />
              ))}
              
              {/* Marketing Component */}
              <div className="public-todo-marketing">
                <div className="public-todo-marketing-content">
                  <h3 className="public-todo-marketing-title">Enjoying this public list?</h3>
                  <p className="public-todo-marketing-text">
                    Get your own, and share with your team! The full free version of SymphonyTodo has way more features, team collaboration, and all the bells and whistles you'd expect from a proper todo app.
                  </p>
                  <a 
                    href="https://symphonytodo.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="public-todo-marketing-button"
                  >
                    <ExternalLink size={18} />
                    Get Started with SymphonyTodo
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Comments Modal - Rendered at top level to avoid clipping */}
      {selectedTodoComments && (
        <div className="public-todo-comments-modal-overlay" onClick={handleCloseComments}>
          <div className="public-todo-comments-modal" onClick={(e) => e.stopPropagation()}>
            <div className="public-todo-comments-header">
              <h3>Comments ({selectedTodoComments.comments.length})</h3>
              <button 
                className="public-todo-comments-close"
                onClick={handleCloseComments}
              >
                ×
              </button>
            </div>
            <div className="public-todo-comments-list">
              {selectedTodoComments.comments.map((comment, index) => (
                <div key={index} className="public-todo-comment-item">
                  <div className="public-todo-comment-author">
                    {getDisplayName(comment.authorEmail)}
                  </div>
                  <div className="public-todo-comment-content">
                    {comment.content}
                  </div>
                  <div className="public-todo-comment-date">
                    {formatDate(comment.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
